import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface AnalyticsData {
  totalConversations: number;
  avgResponseTime: number;
  avgResolutionTime: number;
  activeConversations: number;
  resolvedConversations: number;
  escalatedConversations: number;
  newCustomers: number;
  returningCustomers: number;
  avgMessagesPerConversation: number;
  positiveSentiment: number;
  negativeSentiment: number;
  neutralSentiment: number;
  conversationsByHour: { hour: number; count: number }[];
  conversationsByDay: { date: string; count: number }[];
  messagesByStatus: { status: string; count: number }[];
  topAgentsByResolved: { agentId: string; agentName: string; count: number }[];
}

export const useAnalytics = (dateRange: { from: Date; to: Date }) => {
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const { from, to } = dateRange;

      // Total conversations in range
      const { count: totalConversations } = await supabase
        .from('conversations')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', from.toISOString())
        .lte('created_at', to.toISOString());

      // Conversations by status
      const { data: conversations } = await supabase
        .from('conversations')
        .select('*, messages(created_at, sender_type)')
        .gte('created_at', from.toISOString())
        .lte('created_at', to.toISOString());

      // Count by status
      const activeConversations = conversations?.filter(c => c.status === 'active').length || 0;
      const resolvedConversations = conversations?.filter(c => c.status === 'resolved').length || 0;
      const escalatedConversations = conversations?.filter(c => c.escalation_requested).length || 0;

      // Calculate response times (simplified - time from first customer message to first agent response)
      let totalResponseTime = 0;
      let responseCount = 0;
      let totalResolutionTime = 0;
      let resolutionCount = 0;

      conversations?.forEach(conv => {
        const messages = conv.messages || [];
        const firstCustomerMsg = messages.find(m => m.sender_type === 'customer');
        const firstAgentMsg = messages.find(m => m.sender_type === 'agent');

        if (firstCustomerMsg && firstAgentMsg) {
          const responseTime = new Date(firstAgentMsg.created_at).getTime() - new Date(firstCustomerMsg.created_at).getTime();
          totalResponseTime += responseTime;
          responseCount++;
        }

        if (conv.status === 'resolved' && conv.created_at && conv.updated_at) {
          const resolutionTime = new Date(conv.updated_at).getTime() - new Date(conv.created_at).getTime();
          totalResolutionTime += resolutionTime;
          resolutionCount++;
        }
      });

      const avgResponseTime = responseCount > 0 ? Math.round(totalResponseTime / responseCount / 60000) : 0; // in minutes
      const avgResolutionTime = resolutionCount > 0 ? Math.round(totalResolutionTime / resolutionCount / 60000) : 0; // in minutes

      // Customer metrics
      const uniquePhones = new Set(conversations?.map(c => c.customer_phone));
      const phoneOccurrences = conversations?.reduce((acc, conv) => {
        acc[conv.customer_phone] = (acc[conv.customer_phone] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      const newCustomers = Object.values(phoneOccurrences || {}).filter(count => count === 1).length;
      const returningCustomers = uniquePhones.size - newCustomers;

      // Messages per conversation
      const { data: messages } = await supabase
        .from('messages')
        .select('conversation_id')
        .gte('created_at', from.toISOString())
        .lte('created_at', to.toISOString());

      const avgMessagesPerConversation = totalConversations && messages ? Math.round(messages.length / totalConversations) : 0;

      // Sentiment analysis
      const { data: sentimentMessages } = await supabase
        .from('messages')
        .select('sentiment')
        .gte('created_at', from.toISOString())
        .lte('created_at', to.toISOString())
        .not('sentiment', 'is', null);

      const positiveSentiment = sentimentMessages?.filter(m => m.sentiment === 'positive').length || 0;
      const negativeSentiment = sentimentMessages?.filter(m => m.sentiment === 'negative').length || 0;
      const neutralSentiment = sentimentMessages?.filter(m => m.sentiment === 'neutral').length || 0;

      // Conversations by hour
      const conversationsByHour = Array.from({ length: 24 }, (_, hour) => {
        const count = conversations?.filter(c => {
          const createdHour = new Date(c.created_at).getHours();
          return createdHour === hour;
        }).length || 0;
        return { hour, count };
      });

      // Conversations by day (last 7 days)
      const conversationsByDay = Array.from({ length: 7 }, (_, i) => {
        const date = new Date(to);
        date.setDate(date.getDate() - (6 - i));
        const dateStr = date.toISOString().split('T')[0];
        const count = conversations?.filter(c => {
          const convDate = new Date(c.created_at).toISOString().split('T')[0];
          return convDate === dateStr;
        }).length || 0;
        return { date: dateStr, count };
      });

      // Status distribution
      const messagesByStatus = [
        { status: 'Active', count: activeConversations },
        { status: 'Resolved', count: resolvedConversations },
        { status: 'Waiting', count: conversations?.filter(c => c.status === 'waiting').length || 0 },
      ];

      // Top agents by resolved conversations
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, full_name');

      const agentResolved = conversations
        ?.filter(c => c.status === 'resolved' && c.assigned_agent_id)
        .reduce((acc, conv) => {
          const agentId = conv.assigned_agent_id!;
          acc[agentId] = (acc[agentId] || 0) + 1;
          return acc;
        }, {} as Record<string, number>);

      const topAgentsByResolved = Object.entries(agentResolved || {})
        .map(([agentId, count]) => ({
          agentId,
          agentName: profiles?.find(p => p.id === agentId)?.full_name || 'Unknown',
          count
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      setAnalytics({
        totalConversations: totalConversations || 0,
        avgResponseTime,
        avgResolutionTime,
        activeConversations,
        resolvedConversations,
        escalatedConversations,
        newCustomers,
        returningCustomers,
        avgMessagesPerConversation,
        positiveSentiment,
        negativeSentiment,
        neutralSentiment,
        conversationsByHour,
        conversationsByDay,
        messagesByStatus,
        topAgentsByResolved
      });
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  return { analytics, loading, refetch: fetchAnalytics };
};
